import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { crmTerritorialAPI } from '../../services/api';
import { Loader2 } from 'lucide-react';

// P3H — Configuración mínima de una meta territorial (sección o asesor de esa sección).
export const MetaTerritorialForm = ({ secciones, asesores, seccionActual, onCerrar,
  onGuardado }) => {
  const { toast } = useToast();
  const [seccion, setSeccion] = useState(seccionActual || '');
  const [asesor, setAsesor] = useState('');
  const [inicio, setInicio] = useState('');
  const [fin, setFin] = useState('');
  const [obj, setObj] = useState({ oportunidades: '', cierres: '', monto: '', conversion: '' });
  const [notas, setNotas] = useState('');
  const [enviando, setEnviando] = useState(false);

  const delTerritorio = asesores.filter((a) => a.seccion_id === seccion);
  const algunObjetivo = Object.values(obj).some((v) => Number(v) > 0);
  const listo = seccion && inicio && fin && new Date(inicio) < new Date(fin) && algunObjetivo;

  const guardar = async () => {
    setEnviando(true);
    try {
      await crmTerritorialAPI.crearMeta({
        section_id: seccion,
        asesor_id: asesor || null,
        periodo_inicio: new Date(inicio).toISOString(),
        periodo_fin: new Date(fin).toISOString(),
        objetivo_oportunidades: Number(obj.oportunidades) || 0,
        objetivo_cierres: Number(obj.cierres) || 0,
        objetivo_monto: Number(obj.monto) || 0,
        objetivo_conversion: Number(obj.conversion) || 0,
        notas: notas.trim() || null,
      });
      toast({ title: 'Meta configurada', description: 'Queda registrada en la auditoría.' });
      onGuardado();
      onCerrar();
    } catch (e) {
      toast({ title: 'Meta rechazada', description: e.message, variant: 'destructive' });
    } finally { setEnviando(false); }
  };

  return (
    <Dialog open onOpenChange={onCerrar}>
      <DialogContent className="max-w-2xl" data-testid="meta-territorial-dialog">
        <DialogHeader><DialogTitle>Nueva meta territorial</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select value={seccion} onChange={(e) => { setSeccion(e.target.value); setAsesor(''); }}
                    className="border rounded-lg px-3 py-2 text-sm bg-white"
                    data-testid="meta-seccion">
              <option value="">Sección</option>
              {secciones.map((s) => (
                <option key={s.id} value={s.id}>{s.name} ({s.abbreviation})</option>
              ))}
            </select>
            <select value={asesor} onChange={(e) => setAsesor(e.target.value)}
                    className="border rounded-lg px-3 py-2 text-sm bg-white"
                    data-testid="meta-asesor">
              <option value="">Toda la sección</option>
              {delTerritorio.map((a) => (
                <option key={a.asesor_id} value={a.asesor_id}>{a.nombre}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs text-gray-500">
              Inicio del periodo
              <Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)}
                     data-testid="meta-inicio" />
            </label>
            <label className="text-xs text-gray-500">
              Fin del periodo
              <Input type="date" value={fin} onChange={(e) => setFin(e.target.value)}
                     data-testid="meta-fin" />
            </label>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[['oportunidades', 'Oportunidades'], ['cierres', 'Cierres'], ['monto', 'Monto $'],
              ['conversion', 'Conversión %']].map(([k, etiqueta]) => (
                <label key={k} className="text-xs text-gray-500">
                  {etiqueta}
                  <Input type="number" min="0" value={obj[k]}
                         onChange={(e) => setObj({ ...obj, [k]: e.target.value })}
                         data-testid={`meta-objetivo-${k}`} />
                </label>
            ))}
          </div>
          <Input value={notas} onChange={(e) => setNotas(e.target.value)}
                 placeholder="Notas (opcional)" data-testid="meta-notas" />
          <Button onClick={guardar} disabled={!listo || enviando} data-testid="meta-guardar-btn">
            {enviando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Guardar meta
          </Button>
          <p className="text-xs text-gray-500">
            Al menos un objetivo debe ser mayor que cero. El asesor debe pertenecer a la sección
            elegida. Semáforo determinista: verde ≥100%, ámbar ≥70%, rojo por debajo. Sin correos ni
            alertas externas.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MetaTerritorialForm;
